import { type JSX } from "react";
import { Left } from "@repo/icons";
import { createFileRoute, useParams, useRouter, useSearch } from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types/gql";
import { z } from "zod";
import { HeaderButton, HeaderRoot, HeaderTitle } from "@/components/header";
import { Page } from "@/components/page";
import { useGraphQL } from "@/hooks/use-graphql.ts";
import { PatientAvatar } from "@/components/patient-avatar";
import { AsyncDataWrapper } from "@/components/async-data-wrapper";
import { PatientUpdateForm } from "@/components/patient-detail/patient-update-form.tsx";

export const PatientUpdatePageQuery = graphql(/* GraphQL */ `
  query PatientUpdatePageQuery($patientId: Int!) {
    patient: pacientes_by_pk(codPaciente: $patientId) {
      ...PatientAvatarFragment
      ...PatientUpdateFormQuery
    }
  }
`);

const patientUpdateSchema = z.object({
  action: z.string().optional(),
});

export const PatientUpdatePage = (): JSX.Element => {
  const { patientId } = useParams({ from: "/patients/$patientId/update" });

  const queryResult = useGraphQL(PatientUpdatePageQuery, {
    patientId: Number.parseInt(patientId),
  });
  const { data } = queryResult;
  const router = useRouter();
  const searchParams = useSearch({
    from: "/patients/$patientId/update",
  });

  const isModalOverlayOpen: boolean = searchParams.action === "expand-avatar-image";

  const openModalOverlay = (): void => {
    void router.navigate({
      params: { patientId: String(patientId) },
      search: { ...searchParams, action: "expand-avatar-image" },
    });
  };

  const closeModalOverlay = (): void => {
    if (isModalOverlayOpen) {
      router.history.back();
    }
  };

  const handleOpenModalOverlay = (shouldOpen: boolean): void => {
    shouldOpen ? openModalOverlay() : closeModalOverlay();
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Editar dados" />
      </HeaderRoot>
      <Page className="relative flex flex-col h-full">
        <AsyncDataWrapper {...queryResult}>
          {data?.patient ? (
            <div className="flex flex-col gap-4">
              <PatientAvatar
                shouldOpenModalOverlay
                isModalOverlayOpen={isModalOverlayOpen}
                setIsModalOverlayOpen={() => handleOpenModalOverlay(!isModalOverlayOpen)}
                color="primaryLight"
                data={data.patient}
                size={56}
              />
              <PatientUpdateForm data={data.patient} />
            </div>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/patients/$patientId/update")({
  component: PatientUpdatePage,
  validateSearch: patientUpdateSchema,
});
